/**
 * Generates personalized payment parameters for the payment calculation service.
 *
 * @param {Object} params - The input parameters.
 * @param {Object} params.paymentsInfo - The payment information.
 * @param {number} [params.paymentsInfo.loanTerm] - The loan term in months.
 * @param {string} [params.paymentsInfo.creditType] - The credit type or range.
 * @param {number} [params.paymentsInfo.downPayment] - The down payment amount.
 * @param {number} [params.paymentsInfo.downPaymentPercent] - The down payment percentage.
 * @param {number} [params.paymentsInfo.leaseDownPayment] - The lease down payment amount.
 * @param {Object} [params.paymentsInfo.tradeIn] - The trade-in information.
 * @param {number} [params.paymentsInfo.tradeIn.value] - The trade-in value.
 * @param {number} [params.paymentsInfo.tradeIn.amountOwed] - The amount owed on the trade-in.
 * @param {number} [params.paymentsInfo.leaseTerm] - The lease term in months.
 * @param {number} [params.paymentsInfo.milesPerYear] - The annual mileage for a lease.
 * @param {boolean} [params.paymentsInfo.includeCash] - Whether to include cash in the calculation.
 * @param {boolean} [params.paymentsInfo.includeTaxesFees] - Whether to include taxes and fees in the calculation.
 * @param {string} params.userZip - The user's ZIP code.
 * @param {number} [params.offerPrice] - The offer price of the vehicle.
 * @returns {Object} The personalized payment parameters for the payment calculation service.
 */
const getPersonalizedPaymentParams = _ref => {
  let {
    paymentsInfo,
    userZip,
    offerPrice
  } = _ref;
  const {
    loanTerm,
    creditType,
    downPayment,
    downPaymentPercent,
    leaseDownPayment,
    tradeIn = {},
    leaseTerm,
    milesPerYear,
    includeCash,
    includeTaxesFees
  } = paymentsInfo;
  const {
    value: tradeInValue,
    amountOwed
  } = tradeIn;

  // NOTE: List of available params for payment calulation service:
  // https://ghe.coxautoinc.com/Autotrader/payment-calculation/blob/master/documentation/request.txt
  const paymentParams = {
    // Required service params
    downPayment,
    zip: userZip
  };

  // Optional service params, don't include if null
  if (loanTerm) {
    paymentParams.terms = loanTerm;
  }
  if (!downPayment && downPaymentPercent) {
    paymentParams.downPaymentPercent = downPaymentPercent;
  }
  if (creditType) {
    paymentParams.creditRange = creditType;
  }
  if (leaseTerm) {
    paymentParams.leaseTerms = leaseTerm;
  }
  if (milesPerYear) {
    paymentParams.milesPerYear = milesPerYear;
  }
  if (leaseDownPayment || leaseDownPayment === 0) {
    paymentParams.leaseDownPayment = leaseDownPayment;
  }
  if (includeTaxesFees) {
    paymentParams.includeTaxesFees = true;
  }
  if (includeCash) {
    paymentParams.includeCash = true;
  }
  if (offerPrice) {
    paymentParams.offerPrice = offerPrice;
  }
  if (typeof tradeInValue !== 'undefined') {
    paymentParams.tradeInValue = tradeInValue;
  }
  if (amountOwed) {
    paymentParams.amountOwed = amountOwed;
  }
  return paymentParams;
};
export { getPersonalizedPaymentParams as default };
